
/**
 * Detects entity type and ID from current URL path
 * @returns {Object} Object containing entity type and ID
 */
import store from '../../redux/store';

/**
 * Detects entity type and ID from current URL path
 * @returns {Object} Object containing entity type and ID
 */
export const detectEntityFromURL = () => {
  const pathname = window.location.pathname;
  const state = store.getState();
  const { strategyMapView: smapData, roadmap: roadmapData, timeline } = state;

  let entity = "";
  let entityId = null;

  const isDashboardNewWithId = pathname.match(/\/strategymaps\/[^\/]+/);

  if (isDashboardNewWithId) {
     // Extract ID from path like /strategymaps/some-id
     const matches = pathname.match(/\/strategymaps\/([^\/]+)/);
     const urlEntityId = matches ? matches[1] : '';
     // Use smapData.currentData.id as fallback
     entityId = urlEntityId || smapData?.currentData?.id || "";
     entity = "Strategy Map";
  } else if (pathname.includes("/strategymaps")) {
     entity = "Strategy Map";
     entityId = "";
  } else if (pathname.includes('/workitems') || pathname.includes('/projects')) {
    // Check if it is exactly /projects or /projects/ to detect list view
    const isListView = pathname === '/workitems' || pathname === '/workitems/';    
    if (isListView) {
        entity = "Epics and Features";
        entityId = "";
    } else {
        const id = timeline?.currentEntityId || ""; // Fallback if needed
    
        // Logic from GlobalChatBox:
        const project_data = roadmapData.projectApiPayload;
        const project_type = project_data?.entity_type == "epic" ? "Execution Epic" : "Feature";
        
        entity = project_type;
        entityId = id;
    }
  } 
  else if (pathname.includes('/roadmaps')) {
    const id = timeline?.currentEntityId || ""; // Fallback if needed, though usually projectId is in store/hook
    // Note: In GlobalChatBox.tsx, `project_id` comes from props or URL params hook. 
    // Here we rely on timeline slice or we might need to parse URL if projectId is there.
    // But typically for /projects/:id it might be cleaner to parse URL too if needed.
    // GlobalChatBox uses `project_id` prop which comes from `useParams`.
    // Let's try to get ID from URL if present for projects too.

    const currentEntity = timeline?.currentEntity;

    const isRoadmap = currentEntity === "ROADMAP";
    const isProject = currentEntity === "PROJECT";
    // Note: In GlobalChatBox.tsx, `project_id` comes from props or URL params hook. 
    // Here we rely on timeline slice or we might need to parse URL if projectId is there.
    // But typically for /projects/:id it might be cleaner to parse URL too if needed.
    // GlobalChatBox uses `project_id` prop which comes from `useParams`.
    // Let's try to get ID from URL if present for projects too.

    const project_type = isRoadmap ? "Container Epic" : "Feature";
    // Logic from GlobalChatBox:
    


    entity = project_type;
    entityId = id;

  }
  else if (pathname.includes('/resources')) {
     entity = "resource";
  } else if (pathname.includes("dashboard")) {
     // Legacy dashboard match? GlobalChatBox removed generic "dashboard" else if block for new strategymap logic,
     // but kept it as fallback/other. 
     // GlobalChatBox has `isDashboardNewWithId` check.
     entity = "strategy_map";
     entityId = smapData?.currentData?.id || ""; 
  }else if(pathname === "/") {
    entity = "Epics and Features";
    entityId = "";
}

  return { entity, entityId };
};

/**
 * Maps Redux currentEntity values to backend entity types
 * @param {string} currentEntity - The current entity from Redux
 * @returns {string} Mapped entity type for backend
 */
export const mapCurrentEntityToBackend = (currentEntity: string): string => {
  switch (currentEntity?.toLowerCase()) {
    case 'roadmap':
      return 'strategy_map';
    case 'project':
      return 'work_unit';
    default:
      return currentEntity?.toLowerCase() || 'strategy_map';
  }
};

/**
 * Builds entity payload for resources page based on filter state
 * @param baseEntity - Base entity from chatEntity
 * @param baseEntityId - Base entity ID from chatEntity
 * @param baseEntityName - Base entity name from contextInfo
 * @param capacityPlanningFilters - Filter state from Redux
 * @param organizationTeams - Teams from Redux
 * @param resourcePageSprints - Sprints from Redux
 * @returns Object with entity, entity_id, and entity_name
 */
export const buildResourcesEntityPayload = (
  baseEntity: string | undefined,
  baseEntityId: string | null | undefined,
  baseEntityName: string | undefined,
  capacityPlanningFilters?: {
    searchType?: string;
    selectedTeam?: string;
    selectedSprint?: string;
  },
  organizationTeams?: any[],
  resourcePageSprints?: any[]
): { entity: string; entity_id: string; entity_name: string } => {
  const isResourcesPage = window.location.pathname.includes('/resources');
  
  if (!isResourcesPage) {
    return {
      entity: baseEntity || '',
      entity_id: baseEntityId || '',
      entity_name: baseEntityName || ''
    };
  }

  const { searchType, selectedTeam, selectedSprint } = capacityPlanningFilters || {};

  // Case 1: People/Skills/Task/Project filter -> entity: "resource"
  if (searchType === 'people' || searchType === 'skills' || searchType === 'task' || searchType === 'project') {
    return {
      entity: 'resource',
      entity_id: '',
      entity_name: ''
    };
  }

  // Case 2: Team filter -> entity: 'team', entity_id: <team_id>, entity_name: `Team ${team_name} with project ${project_name}`
  if (searchType === 'team' && selectedTeam) {
    const team = organizationTeams?.find((t: any) => String(t.id) === String(selectedTeam));
    if (team) {
      const teamName = team.name || '';
      const projectName = team.projects?.[0]?.project_name || '';
      const entityName = projectName 
        ? `Team ${teamName} with project ${projectName}`
        : `Team ${teamName}`;
      
      return {
        entity: 'team',
        entity_id: String(team.id),
        entity_name: entityName
      };
    }
  }

  // Case 3: Sprint filter -> entity: 'sprint', entity_name: <sprint_name>, entity_id: based on team selection
  if (searchType === 'sprint' && selectedSprint) {
    // selectedSprint is a composite key "name::status" - extract the sprint name
    const separatorIdx = selectedSprint.lastIndexOf('::');
    const sprintName = separatorIdx >= 0 ? selectedSprint.substring(0, separatorIdx) : selectedSprint;

    let entityId = '';
    
    // If "All Teams" is selected, send empty entity_id
    if (selectedTeam && selectedTeam !== 'ALL_TEAMS') {
      // Find the selected team to get its team_id
      const team = organizationTeams?.find((t: any) => String(t.id) === String(selectedTeam));
      if (team && team.team_id && resourcePageSprints) {
        // Find the sprint that matches both the sprint name and the team's team_id
        const matchingSprint = resourcePageSprints.find((sprint: any) => 
          sprint.name === sprintName && 
          String(sprint.team_id) === String(team.team_id)
        );
        if (matchingSprint && matchingSprint.id) {
          entityId = String(matchingSprint.id);
        }
      }
    }
    
    return {
      entity: 'sprint',
      entity_id: entityId,
      entity_name: sprintName
    };
  }

  // Default: return base payload (for resources page without specific filter)
  return {
    entity: 'resource',
    entity_id: '',
    entity_name: ''
  };
};